import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { GelatoCone, CART_IMG, XXI_LOGO, MOOD_IMG, LogoMark } from "./brand";
import { LocationMap } from "./LocationMap";

const CARDS = [
  {
    id: "location",
    eyebrow: "Come visit",
    title: "Our Locations",
    cta: "View the map",
    action: "map",
    testid: "card-location",
    render: () => (
      <div className="h-full w-full grid place-items-center px-8">
        <LogoMark className="w-[88%] h-auto" viewBox="40 380 920 300" />
      </div>
    ),
  },
  {
    id: "cart",
    eyebrow: "Find us",
    title: "Find Our Gelato Cart",
    cta: "Locate the cart",
    href: "https://manhattan-gelato.emergent.host",
    testid: "card-cart",
    render: () => (
      <img
        src={CART_IMG}
        alt="Stylized golden gelato cart"
        draggable={false}
        className="h-full w-full object-cover select-none pointer-events-none"
      />
    ),
  },
  {
    id: "create",
    eyebrow: "Made to order",
    title: "Create Your Gelato",
    cta: "Create now",
    href: "https://santachiaragelati.com",
    testid: "card-create",
    render: () => (
      <div className="h-full w-full grid place-items-center">
        <GelatoCone className="h-[78%] w-auto drop-shadow-[0_18px_24px_rgba(120,90,40,0.25)]" />
      </div>
    ),
  },
  {
    id: "mood",
    eyebrow: "Flavor pairing",
    title: "Match Your Mood",
    cta: "Find your mix",
    href: "https://mood-gelato.emergent.host/?utm_source=share",
    testid: "card-mood",
    render: () => (
      <img
        src={MOOD_IMG}
        alt="Stylized golden gelato flavour mood selector"
        draggable={false}
        className="h-full w-full object-cover select-none pointer-events-none"
      />
    ),
  },
  {
    id: "xxi",
    eyebrow: "Spiked · XXI",
    title: "The World's Most Awarded Spiked Gelato",
    cta: "Discover XXI",
    href: "https://www.xxigelati.com",
    testid: "card-xxi",
    render: () => (
      <div className="h-full w-full grid place-items-center bg-white p-8">
        <img
          src={XXI_LOGO}
          alt="XXI Spiked Gelato — Santa Chiara"
          draggable={false}
          className="max-h-full w-auto object-contain select-none pointer-events-none"
        />
      </div>
    ),
  },
];

const Card = ({ card, dragging, onMap }) => {
  const open = useCallback(
    (e) => {
      if (dragging.current) {
        e.preventDefault();
        return;
      }
      if (card.action === "map") {
        onMap();
        return;
      }
      window.open(card.href, "_blank", "noopener,noreferrer");
    },
    [card.href, card.action, dragging, onMap]
  );

  return (
    <motion.div
      data-testid={card.testid}
      onClick={open}
      whileHover={{ y: -10 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className="group relative shrink-0 w-[80vw] sm:w-[400px] md:w-[420px] cursor-pointer"
    >
      <div className="relative rounded-[28px] bg-white sc-card-shadow overflow-hidden border border-[#D4AF37]/25">
        {/* Visual */}
        <div className="relative h-[330px] sm:h-[360px] bg-gradient-to-b from-[#DCE7EF] to-[#C4D4DF] overflow-hidden">
          <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-[1.05]">
            {card.render()}
          </div>
          <div className="absolute top-4 left-4">
            <span className="inline-block text-[0.62rem] uppercase tracking-[0.3em] text-[#800020] bg-white/75 backdrop-blur px-3 py-1 rounded-full font-semibold">
              {card.eyebrow}
            </span>
          </div>
        </div>

        {/* Text */}
        <div className="px-7 py-7">
          <div className="gold-rule mb-5 w-12" />
          <h3 className="font-display text-2xl sm:text-[1.7rem] leading-tight text-[#800020] min-h-[4rem] flex items-start">
            {card.title}
          </h3>
          <div className="mt-5 flex items-center justify-between">
            <span className="text-[0.7rem] uppercase tracking-[0.28em] text-[#4A4A4A] font-medium">
              {card.cta}
            </span>
            <span className="h-11 w-11 grid place-items-center rounded-full bg-[#800020] text-white transition-all duration-300 group-hover:bg-[#D4AF37] group-hover:text-[#800020] group-hover:rotate-12">
              <ArrowUpRight className="h-5 w-5" />
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const Carousel = () => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const dragging = useRef(false);
  const x = useMotionValue(0);
  const [maxDrag, setMaxDrag] = useState(0);
  const [active, setActive] = useState(0);
  const [mapOpen, setMapOpen] = useState(false);

  const measure = useCallback(() => {
    if (!viewportRef.current || !trackRef.current) return;
    const diff =
      trackRef.current.scrollWidth - viewportRef.current.offsetWidth;
    setMaxDrag(diff > 0 ? diff : 0);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 400);
    return () => {
      window.removeEventListener("resize", measure);
      clearTimeout(t);
    };
  }, [measure]);

  // Trackpad / touchpad horizontal scrolling (two-finger swipe).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e) => {
      // Only hijack when the gesture is horizontal, so vertical page
      // scrolling over the carousel keeps working normally.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      const next = Math.max(-maxDrag, Math.min(0, x.get() - e.deltaX));
      x.set(next);
      const card = trackRef.current?.firstChild;
      const s = card ? card.offsetWidth + 28 : 440;
      const idx = Math.round(-next / s);
      setActive(Math.max(0, Math.min(CARDS.length - 1, idx)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [maxDrag, x]);

  const step = () => {
    const card = trackRef.current?.firstChild;
    const gap = 28;
    return card ? card.offsetWidth + gap : 440;
  };

  const goTo = (idx) => {
    const clamped = Math.max(0, Math.min(CARDS.length - 1, idx));
    let target = -clamped * step();
    target = Math.max(-maxDrag, Math.min(0, target));
    animate(x, target, { type: "spring", stiffness: 320, damping: 38 });
    setActive(clamped);
  };

  const onDragEnd = () => {
    setTimeout(() => (dragging.current = false), 40);
    const idx = Math.round(-x.get() / step());
    setActive(Math.max(0, Math.min(CARDS.length - 1, idx)));
  };

  return (
    <section data-testid="carousel-section" className="relative">
      <div ref={viewportRef} className="overflow-hidden no-scrollbar">
        <motion.div
          ref={trackRef}
          style={{ x }}
          drag="x"
          dragConstraints={{ left: -maxDrag, right: 0 }}
          dragElastic={0.12}
          onDragStart={() => (dragging.current = true)}
          onDragEnd={onDragEnd}
          className="flex gap-7 px-5 sm:px-8 md:px-16 py-6 cursor-grab active:cursor-grabbing"
        >
          {CARDS.map((card) => (
            <Card
              key={card.id}
              card={card}
              dragging={dragging}
              onMap={() => setMapOpen(true)}
            />
          ))}
        </motion.div>
      </div>

      {/* Controls */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="carousel-dots">
          {CARDS.map((_, i) => (
            <button
              key={i}
              data-testid={`carousel-dot-${i}`}
              aria-label={`Go to card ${i + 1}`}
              onClick={() => goTo(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                active === i
                  ? "w-8 bg-[#800020]"
                  : "w-2 bg-[#800020]/30 hover:bg-[#800020]/60"
              }`}
            />
          ))}
        </div>

        <div className="hidden sm:flex items-center gap-3">
          <button
            data-testid="carousel-prev"
            aria-label="Previous"
            onClick={() => goTo(active - 1)}
            className="h-12 w-12 grid place-items-center rounded-full border border-[#800020]/25 text-[#800020] hover:bg-[#800020] hover:text-white transition-all duration-300 disabled:opacity-30"
            disabled={active === 0}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            data-testid="carousel-next"
            aria-label="Next"
            onClick={() => goTo(active + 1)}
            className="h-12 w-12 grid place-items-center rounded-full border border-[#800020]/25 text-[#800020] hover:bg-[#800020] hover:text-white transition-all duration-300 disabled:opacity-30"
            disabled={active === CARDS.length - 1}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <p className="text-center mt-6 text-[0.7rem] uppercase tracking-[0.3em] text-[#4A4A4A]/70">
        Drag • Swipe to explore
      </p>

      <LocationMap open={mapOpen} onOpenChange={setMapOpen} />
    </section>
  );
};
